import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useCompanyList } from "@/features/company";
import { useDebounce } from "@/hooks/useDebounce";
import { useMoveAccountCompany } from "../hooks/useAdminAccounts";
import type { AdminAccountSearchItem } from "../types/account.types";

interface MoveCompanyDialogProps {
  /** 이동 대상 계정. null이면 닫힘 */
  account: AdminAccountSearchItem | null;
  onClose: () => void;
}

const COMPANY_PAGE_SIZE = 20;

export function MoveCompanyDialog({ account, onClose }: MoveCompanyDialogProps) {
  return (
    <Dialog open={!!account} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>소속 업체 변경</DialogTitle>
          <DialogDescription>
            {account
              ? `${account.accountId} (${account.name}) · 현재 ${account.companyName}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        {/* 대상이 바뀌면 검색 상태를 새로 시작한다 */}
        {account && (
          <MoveCompanyForm
            key={account.accountGuid}
            account={account}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function MoveCompanyForm({
  account,
  onClose,
}: {
  account: AdminAccountSearchItem;
  onClose: () => void;
}) {
  const [keyword, setKeyword] = useState("");
  const [selectedGuid, setSelectedGuid] = useState<string | null>(null);
  const debouncedKeyword = useDebounce(keyword, 300);
  const moveMutation = useMoveAccountCompany();

  const { data, isFetching } = useCompanyList({
    page: 1,
    pageSize: COMPANY_PAGE_SIZE,
    searchStr: debouncedKeyword,
    isActive: null,
  });

  const companies = useMemo(
    () =>
      (data?.dispatchCompanies ?? []).filter(
        (c) => c.companyGuid !== account.companyGuid,
      ),
    [data, account.companyGuid],
  );

  const selected = companies.find((c) => c.companyGuid === selectedGuid) ?? null;

  const handleMove = () => {
    if (!selected) return;
    moveMutation.mutate(
      {
        AccountGuid: account.accountGuid,
        TargetCompanyGuid: selected.companyGuid,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="space-y-3">
      <Input
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        placeholder="옮길 업체명 검색"
        autoFocus
      />

      <div className="h-64 overflow-y-auto rounded-md border border-gray-200">
        {isFetching && companies.length === 0 ? (
          <p className="p-3 text-xs text-gray-500">검색 중…</p>
        ) : companies.length === 0 ? (
          <p className="p-3 text-xs text-gray-500">
            {debouncedKeyword ? "검색 결과가 없습니다." : "업체명을 입력해 주세요."}
          </p>
        ) : (
          <ul>
            {companies.map((company) => {
              const isSelected = company.companyGuid === selectedGuid;
              return (
                <li key={company.companyGuid}>
                  <button
                    type="button"
                    onClick={() => setSelectedGuid(company.companyGuid)}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors ${
                      isSelected
                        ? "bg-blue-50 font-medium text-blue-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span>{company.companyName}</span>
                    <span className="text-[11px] text-gray-400">
                      {company.isActive ? "" : "비활성 "}
                      {company.companyCode ?? ""}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="rounded-md bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800">
        이동하면 <b>이전 업체에서 부여된 역할이 모두 해제</b>됩니다. 회사 스코프 역할을 그대로
        들고 가면 권한 판정이 섞이기 때문입니다. 이동 후 새 업체에서 역할을 다시 부여하세요.
        과거 화물·정산 이력의 등록자 기록은 그대로 남습니다.
      </p>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} className="flex-1 text-xs">
          취소
        </Button>
        <Button
          onClick={handleMove}
          disabled={!selected || moveMutation.isPending}
          className="flex-1 bg-blue-600 text-xs font-bold hover:bg-blue-700"
        >
          {selected ? `${selected.companyName}(으)로 이동` : "업체를 선택하세요"}
        </Button>
      </div>
    </div>
  );
}
